import { useEffect, useState } from 'react'
import AvatarSvg from '../../../components/svg/avatar'
import DealerAvatarSvg from '../../../components/svg/avatar-dealer'
import UserInfoModal from '../../../components/user-info-modal'
import { useAuth } from '../../../contexts/auth'
import useVehiclePageData from '../use-vehicle-page-data'
import Chat from '../../../components/chat'
import Avatar from '../../../components/avatar'

// ----------------------------------------------------------------------

const AUTH_ERROR_MESSAGE = 'Please log in to be able to send messages'
const NOT_VERIFIED_ERROR_MESSAGE = 'Please complete the ID Verification to be able to send messages'
const ErrorType = {
  auth: 'auth',
  notVerified: 'not-verified',
}

// ----------------------------------------------------------------------

export default function Seller({ isUserOwner }) {
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false)
  const [isChatModalOpen, setIsChatModalOpen] = useState(false)
  const [error, setError] = useState(null)

  const [{ isAuthenticated, userDetails }] = useAuth()
  const { vehicleData, sellerInfo, isSellerInfoLoading } = useVehiclePageData()

  const handleChatStart = () => {
    if (!isAuthenticated) setError({ type: ErrorType.auth, message: AUTH_ERROR_MESSAGE })
    else if (!userDetails.verified)
      setError({ type: ErrorType.notVerified, message: NOT_VERIFIED_ERROR_MESSAGE })
    else setIsChatModalOpen(true)
  }

  useEffect(() => {
    if (isAuthenticated && error?.type === ErrorType.auth) {
      setError(null)
    }
  }, [error, isAuthenticated])

  return (
    <div className='seller flex alignCenter'>
      {isUserInfoModalOpen && (
        <UserInfoModal
          info={sellerInfo}
          onClose={() => setIsUserInfoModalOpen(false)}
          onAskQuestion={() => {
            setIsUserInfoModalOpen(false)
            setIsChatModalOpen(true)
          }}
        />
      )}

      <div className='profileImageContainer'>
        <Avatar
          src={sellerInfo.profileImageUrl}
          alt={`seller avatar`}
          width={[76, 60]}
          height={[76, 60]}
          fallbackComponent={sellerInfo.isDealer ? DealerAvatarSvg : AvatarSvg}
        />
      </div>
      <div className='sellerInfo'>
        <div className='name'>{sellerInfo.displayName}</div>

        {!isUserOwner && (
          <>
            <div className='message'>
              Have a question?{' '}
              <button
                disabled={isSellerInfoLoading || !sellerInfo || Boolean(error?.message)}
                className='flatBtn messageBtn'
                onClick={handleChatStart}
              >
                Message seller.
              </button>
            </div>

            <button
              disabled={isSellerInfoLoading || !sellerInfo}
              onClick={() => setIsUserInfoModalOpen(true)}
              className='flatBtn sellerCardBtn'
            >
              View Seller Card
            </button>
          </>
        )}

        {error && <p className='error'>{error.message}</p>}

        {isChatModalOpen && (
          <Chat
            onClose={() => setIsChatModalOpen(false)}
            recipientId={vehicleData.sellerId}
            initialTopic={`${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`}
            vehicleId={vehicleData.id}
          />
        )}
      </div>

      <style jsx>{`
        .seller {
          padding: 1rem 0;
          border-top: 0.1rem solid var(--separator-line-color);
          font-size: 1.4rem;
        }

        .profileImageContainer {
          display: inline-block;
          min-width: 7.6rem;
          width: 7.6rem;
          height: 7.6rem;
          margin: 0 1.6rem 0 0;

          :global(img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            border-radius: 50%;
          }
        }

        .name {
          min-height: 2.4rem;
          font-size: 1.8rem;
          font-weight: 700;
        }

        .messageBtn {
          color: var(--text-link-color);
        }

        .sellerCardBtn {
          margin: 0.3rem 0 0;
          color: var(--text-link-color);
        }

        .error {
          color: var(--alert-color);
          padding: 1rem 0 0;
          font-size: 1.6rem;
        }

        @media (max-width: 1024px) {
          .name {
            font-size: 1.6rem;
          }

          .profileImageContainer {
            min-width: 6rem;
            width: 6rem;
            height: 6rem;
          }
        }
      `}</style>
    </div>
  )
}
